/**
 * Otomatik Mail Raporlari API servisi.
 *
 * Tum istekler `reportsApi` (axios instance) uzerinden yapilir;
 * X-Company-Id + Authorization header'lari otomatik eklenir (bkz: config/apiClient.ts).
 *
 * Backend endpoint'leri:
 *   - /api/Reports/*             — rapor verisi + test-send
 *   - /api/ReportSchedules/*     — schedule CRUD
 *   - /api/ReportSettings/*      — branding ayarlari
 *   - /api/CompanySmtp/*         — SMTP credentials (SuperAdmin)
 *   - /api/ReportRunLogs/*       — calisma loglari
 */

import { reportsApi } from '../../../../config/apiClient';
import type {
  AccessStatus,
  ApiResponse,
  CompanySmtp,
  CompanySmtpUpsertRequest,
  EffectiveSmtp,
  PagedResult,
  ReportRunLog,
  ReportSchedule,
  ReportScheduleUpsertRequest,
  ReportTemplateSettings,
  ReportTemplateSettingsUpsertRequest,
  SuperAdminCompanySummary,
  TestSendRequest,
} from '../types';

// ============================================================================
// REPORT SCHEDULES
// ============================================================================

export async function listSchedules(allCompanies: boolean = false): Promise<ReportSchedule[]> {
  const res = await reportsApi.get<ApiResponse<ReportSchedule[]>>('/api/ReportSchedules/list', {
    params: allCompanies ? { all: true } : undefined,
  });
  return res.data?.Data ?? [];
}

export async function getSchedule(id: number): Promise<ReportSchedule | null> {
  const res = await reportsApi.get<ApiResponse<ReportSchedule>>('/api/ReportSchedules/get', {
    params: { id },
  });
  return res.data?.Data ?? null;
}

export async function createSchedule(payload: ReportScheduleUpsertRequest): Promise<ReportSchedule> {
  const res = await reportsApi.post<ApiResponse<ReportSchedule>>('/api/ReportSchedules/create', payload);
  if (!res.data?.Success || !res.data.Data) {
    throw new Error(res.data?.Message || 'Schedule olusturulamadi');
  }
  return res.data.Data;
}

export async function updateSchedule(id: number, payload: ReportScheduleUpsertRequest): Promise<ReportSchedule> {
  const res = await reportsApi.post<ApiResponse<ReportSchedule>>(
    '/api/ReportSchedules/update',
    payload,
    { params: { id } }
  );
  if (!res.data?.Success || !res.data.Data) {
    throw new Error(res.data?.Message || 'Schedule guncellenemedi');
  }
  return res.data.Data;
}

export async function deleteSchedule(id: number): Promise<void> {
  const res = await reportsApi.post<ApiResponse<unknown>>('/api/ReportSchedules/delete', null, {
    params: { id },
  });
  if (!res.data?.Success) {
    throw new Error(res.data?.Message || 'Schedule silinemedi');
  }
}

// ============================================================================
// REPORT RUN LOGS
// ============================================================================

export async function listRunLogs(params?: {
  scheduleId?: number;
  limit?: number;
  all?: boolean;
}): Promise<ReportRunLog[]> {
  const res = await reportsApi.get<ApiResponse<ReportRunLog[]>>('/api/ReportRunLogs/list', {
    params,
  });
  return res.data?.Data ?? [];
}

/**
 * Server-side paginated logs.
 * Backend: GET /api/ReportRunLogs/list-paged?page=&pageSize=&status=&scheduleId=&all=
 */
export async function listRunLogsPaged(params: {
  page: number;
  pageSize: number;
  status?: 'success' | 'failed' | 'skipped' | null;
  scheduleId?: number | null;
  all?: boolean;
}): Promise<PagedResult<ReportRunLog>> {
  const queryParams: Record<string, string | number | boolean> = {
    page: params.page,
    pageSize: params.pageSize,
  };
  if (params.status) queryParams.status = params.status;
  if (params.scheduleId) queryParams.scheduleId = params.scheduleId;
  if (params.all) queryParams.all = true;

  const res = await reportsApi.get<
    {
      Success: boolean;
      Items?: ReportRunLog[];
      TotalCount?: number;
      Page?: number;
      PageSize?: number;
      Message?: string;
    }
  >('/api/ReportRunLogs/list-paged', { params: queryParams });

  if (!res.data?.Success) {
    throw new Error(res.data?.Message || 'Loglar yüklenemedi');
  }
  return {
    Items: res.data.Items ?? [],
    TotalCount: res.data.TotalCount ?? 0,
    Page: res.data.Page ?? params.page,
    PageSize: res.data.PageSize ?? params.pageSize,
  };
}

// ============================================================================
// REPORT TEMPLATE SETTINGS (Branding)
// ============================================================================

export async function listTemplateSettings(): Promise<ReportTemplateSettings[]> {
  const res = await reportsApi.get<ApiResponse<ReportTemplateSettings[]>>('/api/ReportSettings/list');
  return res.data?.Data ?? [];
}

export async function getTemplateSettings(companyId?: string | 'global' | null): Promise<ReportTemplateSettings | null> {
  const params: Record<string, string> = {};
  if (companyId !== undefined && companyId !== null) {
    params.companyId = companyId;
  }
  const res = await reportsApi.get<ApiResponse<ReportTemplateSettings | null>>('/api/ReportSettings/get', { params });
  return res.data?.Data ?? null;
}

export async function getEffectiveBranding(): Promise<{
  BrandName: string;
  PrimaryColor: string;
  PrimaryDarkColor: string;
  AccentColor: string;
  LogoUrl: string;
  FooterText: string;
}> {
  const res = await reportsApi.get<ApiResponse<any>>('/api/ReportSettings/effective');
  return res.data?.Data ?? {};
}

export async function upsertTemplateSettings(
  payload: ReportTemplateSettingsUpsertRequest
): Promise<ReportTemplateSettings> {
  const res = await reportsApi.post<ApiResponse<ReportTemplateSettings>>(
    '/api/ReportSettings/upsert',
    payload
  );
  if (!res.data?.Success || !res.data.Data) {
    throw new Error(res.data?.Message || 'Sablon kaydedilemedi');
  }
  return res.data.Data;
}

export async function deleteTemplateSettings(id: number): Promise<void> {
  const res = await reportsApi.post<ApiResponse<unknown>>('/api/ReportSettings/delete', null, {
    params: { id },
  });
  if (!res.data?.Success) {
    throw new Error(res.data?.Message || 'Sablon silinemedi');
  }
}

// ============================================================================
// COMPANY SMTP (SuperAdmin)
// ============================================================================

export async function listSmtp(): Promise<CompanySmtp[]> {
  const res = await reportsApi.get<ApiResponse<CompanySmtp[]>>('/api/CompanySmtp/list');
  return res.data?.Data ?? [];
}

export async function getSmtp(companyId?: string | 'global' | null): Promise<CompanySmtp | null> {
  const params: Record<string, string> = {};
  if (companyId !== undefined && companyId !== null) {
    params.companyId = companyId;
  }
  const res = await reportsApi.get<ApiResponse<CompanySmtp | null>>('/api/CompanySmtp/get', { params });
  return res.data?.Data ?? null;
}

export async function upsertSmtp(payload: CompanySmtpUpsertRequest): Promise<CompanySmtp> {
  const res = await reportsApi.post<ApiResponse<CompanySmtp>>('/api/CompanySmtp/upsert', payload);
  if (!res.data?.Success || !res.data.Data) {
    throw new Error(res.data?.Message || 'SMTP kaydedilemedi');
  }
  return res.data.Data;
}

/**
 * Sirket kullanicisinin kendi sirketi icin etkin (resolved) SMTP'sini sifresiz doner.
 * Read-only goruntuleme amacli; SuperAdmin olmayan kullanicilar bunu kullanir.
 */
export async function getEffectiveSmtp(): Promise<EffectiveSmtp | null> {
  const res = await reportsApi.get<ApiResponse<EffectiveSmtp | null>>('/api/CompanySmtp/effective');
  return res.data?.Data ?? null;
}

export async function deleteSmtp(id: number): Promise<void> {
  const res = await reportsApi.post<ApiResponse<unknown>>('/api/CompanySmtp/delete', null, {
    params: { id },
  });
  if (!res.data?.Success) {
    throw new Error(res.data?.Message || 'SMTP silinemedi');
  }
}

// ============================================================================
// TEST SEND (manuel mail gonderme)
// ============================================================================

export interface TestSendResult {
  Subject: string;
  ToCount: number;
  /** Kac sube icin ayri mail gonderildi (coklu sube modu) */
  BranchCount: number;
  /** Mail gonderilen sube ID'leri (veya "ALL" eger Tum Subeler ise) */
  Branches: string[];
  Message?: string;
}

export async function testSend(payload: TestSendRequest): Promise<TestSendResult> {
  const res = await reportsApi.post<
    ApiResponse<unknown> & {
      Subject?: string;
      ToCount?: number;
      BranchCount?: number;
      Branches?: string[];
    }
  >('/api/Reports/test-send', payload);
  if (!res.data?.Success) {
    throw new Error(res.data?.Message || 'Test mail gonderilemedi');
  }
  return {
    Subject: res.data.Subject || '',
    ToCount: res.data.ToCount || 0,
    BranchCount: res.data.BranchCount || 1,
    Branches: res.data.Branches || [],
    Message: res.data.Message,
  };
}

/**
 * HTML onizleme — yeni sekmede acmak icin URL doner.
 * Backend HTML endpoint'i auth header gerektirir; bu nedenle islem axios ile yapilip blob URL'e cevriliyor.
 */
export async function fetchPreviewHtml(params: {
  startDate: string;
  endDate: string;
  branchId?: string | null;
  branchName?: string | null;
  reportType?: string;
  /** Eger doluysa backend cron'a gore periyot hesaplar — startDate/endDate yok sayilir */
  cronExpression?: string | null;
  /** cronExpression dolu ise birlikte kullanilir */
  timeZoneId?: string | null;
}): Promise<string> {
  const res = await reportsApi.get<string>('/api/Reports/end-of-day-html', {
    params,
    responseType: 'text',
  });
  return res.data;
}

// ============================================================================
// SUPER ADMIN — sirket erisim yonetimi
// ============================================================================

export async function listAllCompanies(): Promise<SuperAdminCompanySummary[]> {
  const res = await reportsApi.get<ApiResponse<SuperAdminCompanySummary[]>>(
    '/api/SuperAdminMailReports/companies'
  );
  return res.data?.Data ?? [];
}

export async function toggleCompanyAccess(
  companyId: string,
  enabled: boolean
): Promise<{ CompanyId: string; MailReportsEnabled: boolean }> {
  const res = await reportsApi.post<ApiResponse<{ CompanyId: string; MailReportsEnabled: boolean }>>(
    '/api/SuperAdminMailReports/toggle',
    null,
    { params: { companyId, enabled } }
  );
  if (!res.data?.Success || !res.data.Data) {
    throw new Error(res.data?.Message || 'Toggle basarisiz');
  }
  return res.data.Data;
}

/**
 * Sirket kullanicisi kendi flag'ini sorgulamak icin (sayfada hard-block uyarisini gostermek amaciyla).
 * SuperAdmin tum sirketleri sorgulayabilir.
 */
export async function getAccessStatus(companyId?: string): Promise<AccessStatus | null> {
  const res = await reportsApi.get<ApiResponse<AccessStatus>>(
    '/api/SuperAdminMailReports/access-status',
    { params: companyId ? { companyId } : undefined }
  );
  return res.data?.Data ?? null;
}
